import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmButton from "@/components/ConfirmButton";

const empty = { company_id: "", titulo: "", ghe_setor: "", funcao: "", n_trabalhadores: 0, responsavel: "", observacoes: "", test_type: "nr01_54" };

const INSTRUMENT_OPTIONS = [
  {
    id: "nr01_54",
    name: "NR-01 Sollute",
    tagline: "Instrumento completo Sollute · 54 perguntas",
    description: "Avaliação aprofundada validada para conformidade total com a NR-01. Mantém todos os relatórios já consolidados.",
    questions: 54,
    dimensions: 9,
    time: "12–18 min",
    indication: "Padrão recomendado para diagnóstico completo, auditorias e geração do Relatório AEP.",
    icon: "fa-leaf",
    accent: "from-emerald-500 to-emerald-700",
    border: "border-emerald-200",
    badge: "bg-emerald-50 text-emerald-700",
    label: "Padrão",
  },
  {
    id: "rapido",
    name: "Teste Rápido NR-01",
    tagline: "Triagem ágil · 22 perguntas",
    description: "Diagnóstico preliminar focado nos principais fatores psicossociais. Ideal para triagem rápida.",
    questions: 22,
    dimensions: 7,
    time: "5–8 min",
    indication: "Pequenas empresas, triagem inicial ou aplicação em grande escala.",
    icon: "fa-bolt-lightning",
    accent: "from-amber-500 to-orange-600",
    border: "border-amber-200",
    badge: "bg-amber-50 text-amber-700",
    label: "Rápido",
  },
  {
    id: "copsoq",
    name: "COPSOQ",
    tagline: "Padrão internacional · 40 perguntas",
    description: "Copenhagen Psychosocial Questionnaire III, adaptado para o contexto brasileiro de SST e NR-01.",
    questions: 40,
    dimensions: 10,
    time: "12–18 min",
    indication: "Empresas de médio e grande porte, diagnóstico aprofundado, auditorias de SST.",
    icon: "fa-magnifying-glass-chart",
    accent: "from-indigo-500 to-indigo-700",
    border: "border-indigo-200",
    badge: "bg-indigo-50 text-indigo-700",
    label: "Aprofundado",
  },
  {
    id: "hse",
    name: "HSE Management Standards",
    tagline: "Padrão UK · 35 perguntas",
    description: "Padrões de gestão do estresse ocupacional do Health and Safety Executive (UK), reconhecidos internacionalmente.",
    questions: 35,
    dimensions: 7,
    time: "10–14 min",
    indication: "Indústrias, multinacionais e organizações com cultura de gestão por indicadores.",
    icon: "fa-shield-halved",
    accent: "from-rose-500 to-rose-700",
    border: "border-rose-200",
    badge: "bg-rose-50 text-rose-700",
    label: "Internacional",
  },
];

export default function Assessments({ companyIdOverride }) {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [show, setShow] = useState(false);
  const [pickerStep, setPickerStep] = useState(0); // 0 = instrument picker, 1 = form
  const [form, setForm] = useState(companyIdOverride ? { ...empty, company_id: companyIdOverride } : empty);
  const [view, setView] = useState("avaliacao"); // "avaliacao" | "cnpj"
  const [cnpjBuckets, setCnpjBuckets] = useState([]);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  // Filtros da lista "Por Avaliação" — persistidos em memória, aplicados
  // client-side sobre `items` já carregados.
  const [filters, setFilters] = useState({ search: "", companyId: "", dateFrom: "", dateTo: "", testType: "" });
  const embedded = Boolean(companyIdOverride);

  const load = useCallback(async () => {
    const [a, c] = await Promise.all([api.get("/assessments"), api.get("/companies")]);
    const list = embedded ? a.data.filter((it) => it.company_id === companyIdOverride) : a.data;
    setItems(list);
    setCompanies(c.data);
  }, [embedded, companyIdOverride]);
  const loadCnpj = useCallback(async () => {
    setLoadingCnpj(true);
    try {
      const { data } = await api.get("/cnpj/index");
      // Filter to only show CNPJs that actually have at least one assessment
      setCnpjBuckets((data.items || []).filter((b) => b.assessment_count > 0));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoadingCnpj(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "cnpj") loadCnpj(); }, [view, loadCnpj]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assessments", { ...form, n_trabalhadores: Number(form.n_trabalhadores) || 0 });
      toast.success("Avaliação criada — link anônimo gerado!");
      setShow(false); setPickerStep(0); setForm(empty); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const openPicker = () => {
    setForm(embedded ? { ...empty, company_id: companyIdOverride } : empty);
    setPickerStep(0);
    setShow(true);
  };

  const pickInstrument = (test_type) => {
    setForm({ ...empty, test_type, ...(embedded ? { company_id: companyIdOverride } : {}) });
    setPickerStep(1);
  };

  const selectedInstrument = INSTRUMENT_OPTIONS.find((o) => o.id === form.test_type) || INSTRUMENT_OPTIONS[0];

  // Lista filtrada — busca por título/GHE, empresa, período de criação e instrumento.
  const filteredItems = items.filter((a) => {
    if (filters.companyId && a.company_id !== filters.companyId) return false;
    if (filters.testType && (a.test_type || "nr01_54") !== filters.testType) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${a.titulo || ""} ${a.ghe_setor || ""} ${a.funcao || ""} ${a.company_snapshot?.razao_social || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filters.dateFrom || filters.dateTo) {
      const created = (a.created_at || "").slice(0, 10);
      if (filters.dateFrom && created < filters.dateFrom) return false;
      if (filters.dateTo && created > filters.dateTo) return false;
    }
    return true;
  });
  const hasActiveFilters = Boolean(filters.search || filters.companyId || filters.dateFrom || filters.dateTo || filters.testType);
  const clearFilters = () => setFilters({ search: "", companyId: "", dateFrom: "", dateTo: "", testType: "" });

  const del = async (id) => {
    try {
      await api.delete(`/assessments/${id}`);
      toast.success("Avaliação excluída");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className={embedded ? "" : "p-8 lg:p-12 max-w-7xl mx-auto"}>
      {!embedded && (
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 mb-10">
        <div>
          <div className="overline mb-2">Avaliações psicossociais</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Avaliações</h1>
          <p className="text-slate-600 mt-2">Crie avaliações e gere links anônimos para os colaboradores.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="inline-flex border border-slate-300 bg-white p-1 rounded-xl"
            data-testid="assessments-view-toggle"
          >
            <button
              onClick={() => setView("avaliacao")}
              data-testid="assessments-view-avaliacao"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors ${
                view === "avaliacao"
                  ? "text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              style={view === "avaliacao" ? { background: "var(--so-primary)" } : {}}
            >
              Por avaliação
            </button>
            <button
              onClick={() => setView("cnpj")}
              data-testid="assessments-view-cnpj"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors ${
                view === "cnpj"
                  ? "text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              style={view === "cnpj" ? { background: "var(--so-primary)" } : {}}
            >
              Por CNPJ
            </button>
          </div>
          <button
            onClick={openPicker}
            disabled={companies.length === 0}
            className="btn btn-primary"
            data-testid="assessment-new-button"
          >
            <i className="fa-solid fa-plus"></i> Nova avaliação
          </button>
        </div>
      </div>
      )}

      {embedded && (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900">Avaliações desta empresa</h2>
            <p className="text-xs text-slate-500 mt-1">{items.length} avaliação(ões) registrada(s)</p>
          </div>
          <button
            onClick={openPicker}
            className="btn btn-primary"
            data-testid="assessment-new-button"
          >
            <i className="fa-solid fa-plus"></i> Nova avaliação
          </button>
        </div>
      )}

      {companies.length === 0 && (
        <div className="card mb-6 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-triangle-exclamation text-amber-700"></i>
            <span className="text-amber-900 text-sm">
              Você precisa cadastrar pelo menos uma <Link to="/app/empresas" className="font-bold underline">empresa</Link> antes de criar uma avaliação.
            </span>
          </div>
        </div>
      )}

      <div className="card">
        {!embedded && view === "cnpj" ? (
          loadingCnpj ? (
            <div className="text-slate-500 py-12 text-center text-sm" data-testid="assessments-cnpj-loading">
              <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando agrupamento por CNPJ…
            </div>
          ) : cnpjBuckets.length === 0 ? (
            <div className="text-slate-500 py-12 text-center" data-testid="assessments-cnpj-empty">
              <i className="fa-solid fa-id-card text-4xl mb-4 text-slate-300"></i>
              <p>Nenhuma avaliação consolidada por CNPJ ainda.</p>
              <p className="text-xs mt-1">Cadastre o CNPJ nas empresas e crie avaliações para vê-las aqui.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="assessments-cnpj-table">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3 font-semibold uppercase text-xs tracking-wider">CNPJ</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Razão Social</th>
                  <th className="font-semibold uppercase text-xs tracking-wider text-center">Cadastros</th>
                  <th className="font-semibold uppercase text-xs tracking-wider text-center">Avaliações</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cnpjBuckets.map((b) => (
                  <tr
                    key={b.cnpj}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`assessments-cnpj-row-${b.cnpj}`}
                  >
                    <td className="py-3 font-mono text-xs">{b.cnpj_formatted || b.cnpj}</td>
                    <td className="font-semibold text-slate-900">{b.razao_social}</td>
                    <td className="text-center font-mono">{b.company_count}</td>
                    <td className="text-center font-mono font-bold" style={{ color: "var(--so-primary)" }}>
                      {b.assessment_count}
                    </td>
                    <td className="text-right">
                      <Link
                        to={`/app/cnpj/${b.cnpj}/consolidado`}
                        className="btn btn-secondary text-xs"
                        data-testid={`assessments-cnpj-view-${b.cnpj}`}
                      >
                        <i className="fa-solid fa-layer-group"></i> Ver consolidado
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : items.length === 0 ? (
          <div className="text-slate-500 py-12 text-center">
            <i className="fa-solid fa-clipboard-list text-4xl mb-4 text-slate-300"></i>
            <p>Nenhuma avaliação criada.</p>
          </div>
        ) : (
          <>
            {/* Barra de filtros */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="assessments-filters">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Buscar</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
                  placeholder="Título, GHE, função…"
                  className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                  data-testid="assessments-filter-search"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Empresa</label>
                <select
                  value={filters.companyId}
                  onChange={(e) => setFilters((s) => ({ ...s, companyId: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-emerald-400"
                  data-testid="assessments-filter-company"
                >
                  <option value="">Todas</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.razao_social}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Instrumento</label>
                <select
                  value={filters.testType}
                  onChange={(e) => setFilters((s) => ({ ...s, testType: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-emerald-400"
                  data-testid="assessments-filter-test-type"
                >
                  <option value="">Todos</option>
                  {INSTRUMENT_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">De</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((s) => ({ ...s, dateFrom: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                  data-testid="assessments-filter-date-from"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Até</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((s) => ({ ...s, dateTo: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                  data-testid="assessments-filter-date-to"
                />
              </div>
            </div>
            <div className="mb-3 flex items-center gap-3 text-xs text-slate-500">
              <span data-testid="assessments-filter-count">
                <b className="text-slate-900">{filteredItems.length}</b> de {items.length} avaliações
              </span>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="text-emerald-700 hover:text-emerald-900 font-semibold" data-testid="assessments-filter-clear">
                  <i className="fa-solid fa-xmark mr-1"></i> Limpar filtros
                </button>
              )}
            </div>
            {filteredItems.length === 0 ? (
              <div className="text-slate-500 py-12 text-center border border-dashed border-slate-200 rounded" data-testid="assessments-filter-empty">
                <i className="fa-solid fa-filter-circle-xmark text-3xl mb-3 text-slate-300"></i>
                <p>Nenhuma avaliação corresponde aos filtros.</p>
                <button type="button" onClick={clearFilters} className="mt-2 text-emerald-700 hover:text-emerald-900 font-semibold text-sm">
                  Limpar filtros
                </button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-3 font-semibold uppercase text-xs tracking-wider">Título</th>
                    <th className="font-semibold uppercase text-xs tracking-wider">Empresa</th>
                    <th className="font-semibold uppercase text-xs tracking-wider">GHE / Setor</th>
                    <th className="font-semibold uppercase text-xs tracking-wider text-center">Respostas</th>
                    <th className="font-semibold uppercase text-xs tracking-wider">Criada</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 font-semibold">
                        <Link to={`/app/avaliacoes/${a.id}`} className="hover:text-emerald-900"
                              data-testid={`assessment-row-${a.id}`}>{a.titulo}</Link>
                      </td>
                      <td className="text-slate-600">{a.company_snapshot?.razao_social || "—"}</td>
                      <td className="text-slate-600">{a.ghe_setor || "—"}</td>
                      <td className="text-center font-mono font-bold" style={{ color: "var(--so-primary)" }}>{a.response_count}</td>
                      <td className="text-slate-500 font-mono text-xs">{new Date(a.created_at).toLocaleDateString("pt-BR")}</td>
                      <td className="text-right space-x-2">
                        <Link to={`/app/avaliacoes/${a.id}`} className="btn btn-secondary text-xs"
                              data-testid={`assessment-open-${a.id}`}>Abrir</Link>
                        <ConfirmButton
                          onConfirm={() => del(a.id)}
                          testid={`assessment-delete-${a.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {show && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => { setShow(false); setPickerStep(0); }}>
          {pickerStep === 0 ? (
            /* Step 0 — Instrument picker */
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white border w-full max-w-5xl p-8 rounded-2xl max-h-[90vh] overflow-y-auto"
              data-testid="assessment-instrument-picker"
            >
              <div className="overline mb-2">Nova avaliação · Etapa 1 de 2</div>
              <h2 className="font-display text-2xl font-extrabold mb-1 tracking-tight">Escolha o instrumento</h2>
              <p className="text-slate-600 text-sm mb-6">
                Defina qual questionário psicossocial será aplicado nesta campanha. O instrumento determina as dimensões, perguntas e o cálculo do risco.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {INSTRUMENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => pickInstrument(opt.id)}
                    className={`relative overflow-hidden text-left rounded-2xl border ${opt.border} bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col group`}
                    data-testid={`instrument-${opt.id}`}
                  >
                    <div className={`h-2 bg-gradient-to-r ${opt.accent}`}></div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-3">
                        <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${opt.accent} text-white shadow-md`}>
                          <i className={`fa-solid ${opt.icon} text-base`}></i>
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${opt.badge}`}>
                          {opt.label}
                        </span>
                      </div>
                      <h3 className="font-bold text-slate-900 leading-snug">{opt.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{opt.tagline}</p>
                      <p className="text-sm text-slate-600 mt-2 flex-1">{opt.description}</p>
                      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <dd className="font-bold text-slate-900">{opt.questions}</dd>
                          <dt className="text-slate-500">Questões</dt>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <dd className="font-bold text-slate-900">{opt.dimensions}</dd>
                          <dt className="text-slate-500">Dimensões</dt>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <dd className="font-bold text-slate-900">{opt.time}</dd>
                          <dt className="text-slate-500">Tempo</dt>
                        </div>
                      </dl>
                      <p className="mt-3 text-[11px] text-slate-500 italic">
                        <i className="fa-solid fa-circle-info mr-1 text-slate-400"></i>{opt.indication}
                      </p>
                      <div className={`mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${opt.accent} text-white font-semibold py-2.5 group-hover:shadow-lg transition-shadow`}>
                        Escolher e continuar <i className="fa-solid fa-arrow-right"></i>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => { setShow(false); setPickerStep(0); }}
                  className="btn btn-secondary"
                  data-testid="instrument-picker-cancel"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            /* Step 1 — Form */
            <form onClick={(e) => e.stopPropagation()} onSubmit={save}
                  className="bg-white border w-full max-w-2xl p-8 rounded-2xl" data-testid="assessment-modal">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Nova avaliação · Etapa 2 de 2</div>
                <button
                  type="button"
                  onClick={() => setPickerStep(0)}
                  className="text-xs text-slate-500 hover:text-slate-800"
                  data-testid="assessment-back-to-picker"
                >
                  <i className="fa-solid fa-arrow-left mr-1"></i>Trocar instrumento
                </button>
              </div>
              <h2 className="font-display text-2xl font-extrabold mb-2 tracking-tight">Configurar avaliação</h2>
              <div className={`mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${selectedInstrument.badge}`}>
                <i className={`fa-solid ${selectedInstrument.icon}`}></i>
                <span className="text-sm font-semibold">{selectedInstrument.name}</span>
                <span className="text-xs text-slate-500">· {selectedInstrument.questions} questões · {selectedInstrument.dimensions} dimensões</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="label">Empresa *</label>
                  {embedded ? (
                    <input
                      type="text"
                      readOnly
                      value={companies.find((c) => c.id === companyIdOverride)?.razao_social || "Empresa atual"}
                      className="input bg-slate-100 cursor-not-allowed"
                      data-testid="assessment-field-company-locked"
                    />
                  ) : (
                    <select required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                            className="input" data-testid="assessment-field-company">
                      <option value="">Selecione…</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
                    </select>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="label">Título da avaliação *</label>
                  <input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                         placeholder="Ex.: Avaliação Psicossocial 2026 - Operações" className="input"
                         data-testid="assessment-field-titulo" />
                </div>
                <div>
                  <label className="label">GHE / Setor</label>
                  <input value={form.ghe_setor} onChange={(e) => setForm({ ...form, ghe_setor: e.target.value })}
                         className="input" data-testid="assessment-field-ghe" />
                </div>
                <div>
                  <label className="label">Função</label>
                  <input value={form.funcao} onChange={(e) => setForm({ ...form, funcao: e.target.value })}
                         className="input" data-testid="assessment-field-funcao" />
                </div>
                <div>
                  <label className="label">Nº de trabalhadores</label>
                  <input type="number" min="0" value={form.n_trabalhadores}
                         onChange={(e) => setForm({ ...form, n_trabalhadores: e.target.value })}
                         className="input" data-testid="assessment-field-n" />
                </div>
                <div>
                  <label className="label">Responsável</label>
                  <input value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                         className="input" data-testid="assessment-field-resp" />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Observações</label>
                  <textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                            className="input min-h-[80px]" data-testid="assessment-field-obs" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => { setShow(false); setPickerStep(0); }} className="btn btn-secondary"
                        data-testid="assessment-modal-cancel">Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="assessment-modal-save">
                  <i className="fa-solid fa-bolt"></i> Criar e gerar link
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
